'use client';
import React, { useState } from 'react';
import type { FeeByYearGroup } from '@/data/schools';

interface FeeBreakdownTableProps {
  fees: FeeByYearGroup[];
  isMobile?: boolean;
}

export default function FeeBreakdownTable({ fees, isMobile = false }: FeeBreakdownTableProps) {
  const [showTransport, setShowTransport] = useState(false);

  // Find the year group with the lowest and highest annual fee
  const sortedByFee = [...fees].sort((a, b) => a.annualFeeAed - b.annualFeeAed);
  const lowestFeeRow = sortedByFee[0];
  const highestFeeRow = sortedByFee[sortedByFee.length - 1];

  const displayRows = lowestFeeRow && highestFeeRow && lowestFeeRow.yearGroup !== highestFeeRow.yearGroup
    ? [
        { ...lowestFeeRow, label: 'Lowest' },
        { ...highestFeeRow, label: 'Highest' },
      ]
    : lowestFeeRow
    ? [{ ...lowestFeeRow, label: 'All Year Groups' }]
    : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-display font-semibold text-sm text-foreground">Annual Fees by Year Group</h4>
        {/* Transport toggle — desktop/tablet only */}
        {!isMobile && (
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setShowTransport(!showTransport)}
              className={`w-9 h-5 rounded-full relative transition-all duration-200 cursor-pointer ${showTransport ? 'bg-primary' : 'bg-border'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${showTransport ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-xs text-muted-foreground">Include transport</span>
          </label>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Year Group</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tuition (AED)</th>
              {showTransport && !isMobile && <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Transport</th>}
              <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Total / Year</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, i) => {
              const total = row.annualFeeAed + (showTransport && !isMobile ? row.transportFeeAed : 0);
              return (
                <tr
                  key={`fee-row-${row.label}`}
                  className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/20'}`}
                >
                  <td className="px-4 py-3 font-medium text-foreground">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground block">{row.label}</span>
                      <span className="text-sm">{row.yearGroup}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">{row.annualFeeAed.toLocaleString()}</td>
                  {showTransport && !isMobile && (
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{row.transportFeeAed.toLocaleString()}</td>
                  )}
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">{total.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}